import { z } from "zod";
import { PaginationSchema } from "../../pagination/response/PaginationResponse";

/**
 * One Billing connection record (links this PMS to the central Billing system).
 *
 * SECURITY: the API never returns the connection key (PMS stores only a hash).
 * `z.object` strips unknown keys, so even if a key-like field ever slipped into
 * the payload it would not survive parsing / reach the UI cache.
 *
 * Datetime fields use plain `z.string()` (not `.datetime()`) on purpose — Laravel
 * may serialise with microseconds/offsets and a strict format must not blank the page.
 */
const RawBillingConnectionSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    code: z.string(),
    key_hint: z.string().nullable().optional(),
    scope: z.enum(["company", "property"]),
    is_active: z.boolean(),
    company_id: z.string().nullable(),
    company_name: z.string().nullable().optional(),
    property_id: z.string().nullable(),
    property_name: z.string().nullable().optional(),
    // B2C only; false = the connected property is inactive. Absent on an older backend.
    property_active: z.boolean().nullable().optional(),
    last_pulled_at: z.string().nullable(),
    created_at: z.string().nullable(),
});

/** Maps the API shape (`scope`, `company_name`/`property_name`) to what the UI uses. */
export const BillingConnectionSchema = RawBillingConnectionSchema.transform((c) => ({
    id: c.id,
    name: c.name,
    code: c.code,
    key_hint: c.key_hint ?? null,
    is_active: c.is_active,
    scope_type: c.scope,
    company_id: c.company_id,
    property_id: c.property_id,
    property_inactive: c.scope === "property" && c.property_active === false,
    scope_name: (c.scope === "company" ? c.company_name : c.property_name) ?? null,
    last_pulled_at: c.last_pulled_at,
    created_at: c.created_at,
}));

/**
 * GET /v4/billing-connections
 *
 * With `?all=1` the API returns a plain array; without it, `data` is the current
 * page plus a `pagination` object (15 rows by default, `limit` up to 100). The
 * nested-paginator shape is also accepted and normalised to an array.
 */
export const IndexBillingConnectionResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number().int(),
    data: z.union([
        z.array(BillingConnectionSchema),
        z.object({ data: z.array(BillingConnectionSchema) }).passthrough().transform((page) => page.data),
    ]),
    pagination: z.nullable(PaginationSchema).optional(),
});

/** POST /v4/billing-connections and PATCH /v4/billing-connections/{id} */
export const BillingConnectionMutationResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number().int(),
    data: BillingConnectionSchema.nullable().optional(),
});

/** DELETE /v4/billing-connections/{id} */
export const DeleteBillingConnectionResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number().int(),
    data: z.any().optional(),
});

export type BillingConnectionData = z.infer<typeof BillingConnectionSchema>;
export type IndexBillingConnectionResponse = z.infer<typeof IndexBillingConnectionResponseSchema>;
export type BillingConnectionMutationResponse = z.infer<typeof BillingConnectionMutationResponseSchema>;
